/**
 * Shell copy for `src/components` (keys: `shell.components.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  advancedMode: {
    advancedMode: 'Advanced mode',
    labelEnabled: '{{label}} enabled',
    labelDisabled: '{{label}} disabled',
  },
  brandKitPicker: {
    none: 'None',
    default: ' (default)',
  },
  commerceProductPicker: {
    connectACatalogShopifyWoocommerceOr: 'Connect a catalog (Shopify, WooCommerce or Meta catalog) and sync it to choose products.',
    couldNotLoadTheCatalog: 'Could not load the catalog.',
    searchAProductTitleBrandSku: 'Search a product (title, brand, SKU)…',
    searchAProduct: 'Search a product',
    refreshTheCatalog: 'Refresh the catalog',
    loadingTheCatalog: 'Loading the catalog…',
    noProductMatchesQuery: 'No product matches “{{query}}”.',
    theCatalogFeatureIsNotYet: 'The catalog feature is not yet available on this server.',
    noProductInYourCatalog: 'No product in your catalog',
    previous: '← Previous',
    pagePagePagecountTotalProductS: 'Page {{page}} / {{pageCount}} · {{total}} product(s)',
    next: 'Next →',
    maxSelected: '/ {{max}} selected',
    maximumReached: ' — maximum reached',
  },
  currencySelector: {
    currency: 'Currency',
  },
  diagramEditor: {
    couldNotLoadTheDiagramStatus: 'Could not load the diagram ({{status}}).',
    couldNotReadTheDiagram: 'Could not read the diagram.',
    couldNotLoadTheDiagram: 'Could not load the diagram.',
    theDiagramExportFailed: 'The diagram export failed.',
    couldNotSaveTheDiagram: 'Could not save the diagram.',
    diagramEditor: 'Diagram editor',
    drawDragAndDropThenSave: 'Draw, drag and drop, then “Save” — the image stays re-editable.',
    close: 'Close',
    savingTheDiagram: 'Saving the diagram…',
    loadingTheEditor: 'Loading the editor…',
  },
  diagramRenderForm: {
    item: 'Item',
    diagram: 'Diagram',
    generateADiagramExactTemplate: 'Generate a diagram (exact template)',
    close: 'Close',
    model: 'Model',
    chooseATemplate: '— Choose a template —',
    noTemplateSelected: 'No template selected.',
    loadingTheTemplate: 'Loading the template…',
    remove: 'Remove',
    addV: '+ Add {{v}}',
    format: 'Format',
    generating: 'Generating…',
    generateTheDiagram: '🧩 Generate the diagram',
    preview: 'Preview',
    diagramGeneratedAndAttachedToThe: 'Diagram generated and attached to the post.',
    svgPreviewShownRasterizationEngineUnavai: 'SVG preview shown (rasterization engine unavailable — nothing was attached).',
    noPreviewAvailable: 'No preview available.',
    generatedDiagram: 'Generated diagram',
    title: 'Title',
  },
  generationBrief: {
    video: 'Video',
    flyer: 'Flyer',
    gif: 'GIF',
    removeValue: 'Remove {{value}}',
    topicCreativeDirection: 'Topic / creative direction',
    eGAngleAndIntentOf: 'E.g. angle and intent of the content to produce',
    toneStyle: 'Tone / style',
    eGExpertWarmDirect: 'E.g. expert, warm, direct',
    targetAudience: 'Target audience',
    eGWomenExecutivesOfB2b: 'E.g. women executives of B2B SMEs',
    keywordsToInclude: 'Keywords to include',
    enterOrCommaToAdd: 'Enter or comma to add',
    addAKeyword: 'Add a keyword…',
    constraintsToRespect: 'Constraints (to respect)',
    eGIncludeACallTo: 'E.g. include a call to action…',
    toAvoidNegatives: 'To avoid (negatives)',
    eGJargonSuperlatives: 'E.g. jargon, superlatives…',
    neverMention: 'Never mention',
    theseTermsAreRemovedFromThe: 'These terms are removed from the generated text',
    eGACompetitorSName: 'E.g. a competitor\'s name…',
    format: 'Format',
    eGStorySquarePost: 'E.g. story, square post',
    dimensions: 'Dimensions',
    eG1080x1080: 'E.g. 1080x1080',
    maxLengthCharacters: 'Max length (characters)',
    eG280: 'E.g. 280',
    durationSeconds: 'Duration (seconds)',
    forVideoGifProductions: 'For video / GIF productions',
    eG15: 'E.g. 15',
    visualDescriptionsByType: 'Visual descriptions (by type)',
    whatTheVVisualShouldDepict: 'What the {{v}} visual should depict…',
  },
  insufficientCreditsBanner: {
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance',
    yourPersonalMonthlyCapHasBeen: 'Your personal monthly cap has been reached',
    capCr: ' ({{cap}} cr)',
    askAnAdministratorToRaiseIt: '— ask an administrator to raise it. Topping up the wallet changes nothing for you.',
    requiredRequiredCrAvailableAvailableCr: 'Required {{required}} cr · available {{available}} cr',
    topUpMyCredits: 'Top up my credits',
    close: 'Close',
  },
  mediaAttacher: {
    importAUrl: 'Import a URL',
    remove: 'Remove',
    moveUp: 'Move up',
    moveEarlier: 'Move earlier',
    moveDown: 'Move down',
    moveLater: 'Move later',
    altText: 'Alt text…',
    altTextForMediaV: 'Alt text for media {{v}}',
    media: 'Media',
    add: '+ Add',
    noMediaAttached: 'No media attached.',
    limitOfMaxmediaMediaReached: 'Limit of {{maxMedia}} media reached.',
    limitReachedMaxmediaMediaMaximumFor: 'Limit reached: {{maxMedia}} media maximum for the selected platforms.',
    directLink: 'Direct link',
    upload: 'Upload',
    library: 'Library',
    generateAi: 'Generate (AI)',
    designer: 'Designer',
    cancel: 'Cancel',
    add2: 'Add',
    close: 'Close',
    addMedia: 'Add media',
    mediaSource: 'Media source',
    mediaAddedContinueOrCloseThis: 'media added. Continue or close this window.',
    mediaUrl: 'Media URL',
    altTextAccessibility: 'Alt text (accessibility)',
    couldNotLoadTheMediaLibrary: 'Could not load the media library.',
    searchTheMediaLibrary: 'Search the media library…',
    searchMedia: 'Search media',
    search: 'Search',
    noMediaUploadAFileFlyer: 'No media. Upload a file (flyer, image, video, PDF…) — it will appear here for reuse.',
    adding: 'Adding…',
    mediaUrlMissingFromTheResponse: 'Media URL missing from the response.',
    uploadFailed: 'Upload failed.',
    dragAndDropYourFilesHere: 'Drag and drop your files here',
    imagesVideosGifsPdfsMultipleFiles: 'images, videos, GIFs, PDFs… — multiple files accepted',
    chooseFiles: 'Choose files',
    failed: 'Failed',
    added: 'Added ✓',
    image: 'Image',
    video: 'Video',
    event: 'Event',
    theServiceReturnedVisualSuggestionsBut: 'The service returned visual suggestions but no image. Try again or enable image generation on the backend.',
    noImageWasGeneratedTryAgain: 'No image was generated. Try again with a more descriptive prompt.',
    noVideoGenerated: 'No video generated.',
    aiGenerationFailedAiServiceUnavailable: 'AI generation failed (AI service unavailable?).',
    mediaType: 'Media type',
    comingSoonNotSelectable: ' (coming soon — not selectable)',
    comingSoon: 'Coming soon',
    describeTheVisualYouWant: 'Describe the visual you want',
    eGMinimalistIllustrationOfAn: 'E.g. minimalist illustration of an analytics dashboard, blue tones',
    numberOfImages: 'Number of images',
    visualStyle: 'Visual style',
    generating: 'Generating…',
    generate: 'Generate',
    clickToAttach: 'Click to attach',
    attachThisImage: 'Attach this image',
    attachThisVideo: 'Attach this video',
    attach: '+ Attach',
    noImageOgImageFoundOn: 'No image (og:image) found on this page.',
    couldNotImportThisUrl: 'Could not import this URL.',
    pageUrlArticleProductTweet: 'Page URL (article, product, tweet…)',
    analyzing: 'Analyzing…',
    fetchThePreview: 'Fetch the preview',
    attachTheImage: 'Attach the image',
    createAVisualInTheGraphic: 'Create a visual in the graphic designer, export it, then attach it to your post.',
    theDesignerOpensAfterExportPng: 'The designer opens; after “Export PNG”, a “Attach to post” button brings you back here with the visual attached.',
    openTheDesigner: 'Open the designer',
    saveYourPostFirstToEnable: 'Save your post first to enable the automatic return. Meanwhile, open the designer in a new tab — exports land in the media library, reusable via “Import a URL” or “Direct link”.',
    openTheDesignerNewTab: 'Open the designer (new tab)',
    httpsExampleComArticle: 'https://example.com/article',
  },
  pwaInstallPrompt: {
    installTheApp: 'Install the app',
    installKarexia: 'Install Karexia',
    reachYourContactsAndCampaignsFaster: 'Reach your contacts and campaigns faster, even offline.',
    onIphoneIpadTap: 'On iPhone/iPad: tap',
    share: 'Share',
    then: 'then',
    addToHomeScreen: '“Add to Home Screen”',
    installTheApp2: 'Install the app',
    later: 'Later',
    close: 'Close',
  },
  visualTemplateGallery: {
    architecture: 'Architecture',
    diagram: 'Diagram',
    comparisonData: 'Comparison & data',
    progress: 'Progress',
    previewOfTheLabelTemplate: 'Preview of the {{label}} template',
    visualTemplateGallery: 'Visual template gallery',
    chooseAVisualStyle: 'Choose a visual style',
    closeTheTemplateGallery: 'Close the template gallery',
    close: '✕ Close',
    loadingTemplates: 'Loading templates…',
    visualTemplatesAreNotAvailableRight: 'Visual templates are not available right now.',
    noVisualTemplateAvailable: 'No visual template available.',
    automaticLetTheAiChooseThe: '✨ Automatic — let the AI choose the best style',
    animatedTemplate: 'Animated template',
    optimizedForAnotherLevel: 'Optimized for another level',
    suitedToThisLevel: 'Suited to this level',
  },
};

export default en;
